import { useEffect } from 'react';
import styled from 'styled-components';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { getSubdomainFromLocale, getFolderPrefixFromLocale } from 'developer-docs-shared-components/lib/locale/utils';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import Small from 'UIComponents/elements/Small';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Paths } from './RoutesPublic';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import HubSpotDevLogo from './HubSpotDevLogo';
import { EERIE, SLINKY } from 'HubStyleTokens/colors';
import { MANAGE_COOKIES_CLASSNAME, renderManageCookiesLink } from './GDPRCompliance';
import { useMemo } from 'react';
import { StyledFooter } from '../utils/css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const Link = styled(UILink).withConfig({
  displayName: "Footer__Link",
  componentId: "aouf67-0"
})(["color:", ";font-weight:500;", "{display:block;padding:5px 0;}"], SLINKY, mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT));
const UL = styled.ul.withConfig({
  displayName: "Footer__UL",
  componentId: "aouf67-1"
})(["list-style:none;padding:0;margin:0;"]);
const LI = styled.li.withConfig({
  displayName: "Footer__LI",
  componentId: "aouf67-2"
})(["margin:13px 0;", "{display:inline-block;margin:0 34px;&:first-child{margin-left:0;}}", "{margin:0 10px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.betweenWidths(MOBILE_MENU_BREAKPOINT, 1600));

/**
 * Wraps the logo and `<ul />` into columns at desktop.
 */
const ColumnWrapper = styled.div.withConfig({
  displayName: "Footer__ColumnWrapper",
  componentId: "aouf67-3"
})(["margin-top:20px;padding-top:20px;display:flex;flex-direction:row;justify-content:space-between;border-top:1px solid ", ";"], EERIE);
const Copyright = styled(Small).withConfig({
  displayName: "Footer__Copyright",
  componentId: "aouf67-4"
})(["display:block;line-height:12px;"]);
const LogoWrapper = styled.div.withConfig({
  displayName: "Footer__LogoWrapper",
  componentId: "aouf67-5"
})(["", "{margin-top:10px;}"], mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT));

/**
 * Shared props among the FormattedJSXMessage nodes in the footer links.
 */
const sharedLinkProps = {
  elements: {
    Link
  }
};
const Footer = ({
  width = 'article',
  inline = true,
  showGDPRNode = true,
  additionalContent
}) => {
  useEffect(() => {
    renderManageCookiesLink();
  }, []);
  const locale = useLocale();

  /**
   * External link to Developer home.
   *
   * TODO: Pass along locale to href, e.g. DE is https://developers.hubspot.de
   */
  const homeLinkNode = /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedLinkProps, {
    message: "developer-docs-ui.footer.homeLink_jsx",
    options: {
      href: getSubdomainFromLocale(locale)
    }
  }));
  const folderLocalePrefix = useMemo(() => {
    const folderPrefix = getFolderPrefixFromLocale(locale);
    return folderPrefix ? `/${folderPrefix}` : '';
  }, [locale]);

  /**
   * The logo and unordered list of links. Shared markup across desktop and
   * mobile.
   */
  const footerContents = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(LogoWrapper, {
      children: [/*#__PURE__*/_jsx(UIRouterLink, {
        to: Paths.HOME,
        children: /*#__PURE__*/_jsx(HubSpotDevLogo, {
          width: 150,
          title: I18n.text('developer-docs-ui.accessibility.homeLink')
        })
      }), /*#__PURE__*/_jsx(Copyright, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-ui.footer.copyright",
          options: {
            year: new Date().getFullYear().toString()
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(UL, {
      children: [additionalContent, /*#__PURE__*/_jsx(ResponsiveRenderer, {
        renderMobile: () => /*#__PURE__*/_jsx(LI, {
          children: homeLinkNode
        }),
        renderDesktop: () => null
      }), /*#__PURE__*/_jsx(LI, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedLinkProps, {
          message: "developer-docs-ui.footer.legal_jsx",
          options: {
            href: `https://legal.hubspot.com${folderLocalePrefix}/legal-stuff`
          }
        }))
      }), /*#__PURE__*/_jsx(LI, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedLinkProps, {
          message: "developer-docs-ui.footer.privacyPolicy_jsx",
          options: {
            href: `https://legal.hubspot.com${folderLocalePrefix}/privacy-policy`
          }
        }))
      }), showGDPRNode && /*#__PURE__*/_jsx(LI, {
        "data-test-id": "gdpr-list-item",
        className: MANAGE_COOKIES_CLASSNAME,
        hidden: true,
        children: /*#__PURE__*/_jsx(Link, {})
      })]
    })]
  });
  return /*#__PURE__*/_jsx(StyledFooter, {
    "data-test-id": "docs-public-footer",
    $width: width,
    inline: inline,
    children: /*#__PURE__*/_jsx(ResponsiveRenderer, {
      renderMobile: () => footerContents,
      renderDesktop: () => /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          className: "text-right",
          children: homeLinkNode
        }), /*#__PURE__*/_jsx(ColumnWrapper, {
          children: footerContents
        })]
      })
    })
  });
};
export default Footer;